import type { ReactNode } from 'react';
import { MarketingShell } from '../components/MarketingShell';

const LAST_UPDATED = '12 May 2026';

// Privacy policy for mydyslexiatools.com and the Answer Assist app.
// Drafted to satisfy: UK General Data Protection Regulation (UK GDPR), the UK Age Appropriate
// Design Code (Children's Code), and Apple App Store + Google Play privacy disclosure
// requirements. Plain English. Reviewed by a real solicitor before App Store submission.
export function PrivacyScreen() {
  return (
    <MarketingShell>
      <article className="max-w-3xl mx-auto px-6 py-12 prose-content">
        <h1 className="text-3xl font-semibold tracking-tight">Privacy Policy</h1>
        <p className="text-ink/50 text-sm mt-1">Last updated: {LAST_UPDATED}</p>

        <Section title="Who we are">
          <p>
            Answer Assist and My Dyslexia Tools are products of Creative Sauce Ltd, a company
            registered in England and Wales. Our public address for contact is{' '}
            <a className="text-accent hover:underline" href="mailto:support@mydyslexiatools.com">
              support@mydyslexiatools.com
            </a>
            . If you would prefer to write to us by post, ask us by email and we will share our
            registered office address.
          </p>
        </Section>

        <Section title="The short version">
          <ul>
            <li>
              If you create an account so your worksheets save across devices, we store your
              email address and a hashed password. Nothing more is required.
            </li>
            <li>We do not store the photos you take.</li>
            <li>We do not store the text of your questions or answers.</li>
            <li>
              We do collect anonymous usage events (for example "a worksheet was captured")
              so we can make the app better. You can turn this off in Settings.
            </li>
            <li>
              If you choose to use AI mode, the photo is sent to our server, processed, and
              immediately deleted. No copies are kept.
            </li>
          </ul>
        </Section>

        <Section title="The data we collect">
          <h3>1. Account (only if you create one)</h3>
          <p>
            If you choose to create an account so your worksheets save across devices, we store:
          </p>
          <ul>
            <li>
              <strong>Your email address.</strong> Used to sign you in, to reset your password if
              you forget it, and to contact you if your account needs attention (for example a
              security notice). We do not send marketing emails unless you opt in separately.
            </li>
            <li>
              <strong>A hashed version of your password.</strong> We never store the plain
              password. We could not see it even if we tried.
            </li>
            <li>
              <strong>An optional display name</strong> if you choose to add one. You do not
              have to.
            </li>
          </ul>
          <p>
            You can use the app without an account. In that case nothing on this list is
            collected. You can delete your account at any time and we will remove the records
            within thirty days.
          </p>

          <h3>2. Anonymous usage events</h3>
          <p>
            When you use the app, your device generates a random identifier (a string of letters
            and numbers) that stays on your device. We use it to count how many distinct devices
            use a feature. The identifier cannot be linked to you. It contains no name, email,
            location, phone number, or anything else personal.
          </p>
          <p>
            Each event records what happened, when, on what platform (iPhone, Android, web),
            and the anonymous identifier. Examples:
          </p>
          <ul>
            <li>
              <em>app_opened</em>: someone opened the app.
            </li>
            <li>
              <em>worksheet_capture_started</em>: someone tapped the camera button.
            </li>
            <li>
              <em>worksheet_capture_rejected</em> with a reason such as "face_detected" or
              "low_quality": the safeguarding gate refused a photo.
            </li>
            <li>
              <em>worksheet_processed</em>: the vision pipeline returned a result, with the
              number of questions found (a number, never the question text).
            </li>
            <li>
              <em>worksheet_exported</em>: a finished sheet was saved as PDF or image.
            </li>
          </ul>
          <p>
            We never record the photo, the worksheet questions, the answers, or the audio.
          </p>

          <h3>3. AI vision (on by default, you can turn it off)</h3>
          <p>
            AI mode is on by default because it is what makes the worksheet detection accurate.
            The first time you open the app we show a clear notice explaining this and how to
            switch it off. The same safeguarding rules apply either way: no body images, no
            faces, documents only.
          </p>
          <p>
            When AI mode is on, the photo you just captured is sent over an encrypted connection
            to our serverless function, which forwards it to a vision model provided by
            Anthropic. The model returns a structured map of the questions on the page and
            where the answers should go. The photo is then immediately discarded. Our server
            does not store the photo to disk. Anthropic, as our processor, does not use this
            photo to train models.
          </p>
          <p>
            You can switch AI mode off at any time from Settings. The app then runs entirely
            on your device, with reduced accuracy on slot detection.
          </p>

          <h3>4. Audio (your dictated answers)</h3>
          <p>
            When you tap "Speak my answer", your microphone audio is transcribed by your
            browser or device's built-in speech recognition. On iPhone this is provided by
            Apple. On Android, Chrome, and Edge it is provided by Google or Microsoft. The audio
            is sent to those vendors for transcription, not to us. We only ever see the
            transcribed text, never the audio.
          </p>
          <p>
            On most modern devices the transcription happens on the device with no network
            transfer. Where that is not possible, the vendor handles it. Their privacy policies
            apply to that step. Our app does not record, store, or send any audio.
          </p>

          <h3>5. What we do NOT collect</h3>
          <ul>
            <li>No names, beyond the optional display name you choose to enter yourself.</li>
            <li>No date of birth.</li>
            <li>No phone numbers.</li>
            <li>No physical addresses.</li>
            <li>No precise location. No IP-based geolocation in the app.</li>
            <li>No contacts, photo library, or anything outside the app.</li>
            <li>No tracking pixels, no advertising identifiers, no third-party analytics.</li>
            <li>No worksheet content. No saved photos. No saved answers on our servers.</li>
          </ul>
        </Section>

        <Section title="Children's data (the Age Appropriate Design Code)">
          <p>
            Answer Assist is built for children with dyslexia. We follow the UK Information
            Commissioner's Age Appropriate Design Code (also called the Children's Code) and
            the equivalent General Data Protection Regulation for Kids (GDPR-K) requirements.
            That means:
          </p>
          <ul>
            <li>
              Privacy is set to the strongest by default. The cloud AI mode is OFF unless you
              choose to switch it on.
            </li>
            <li>
              We collect the minimum data needed for the app to work. No nudging the user to
              share more.
            </li>
            <li>No profiling. No advertising. No retargeting. Ever.</li>
            <li>
              Safeguarding is built in. The camera rejects photos of people, the answers a child
              dictates are scanned for crisis or distress language and the app responds with
              calm, age-appropriate signposting to support services.
            </li>
            <li>
              The app does not nag for in-app purchases. The purchase, when one exists, is a
              single one-off unlock, never a subscription that auto-renews.
            </li>
          </ul>
          <p>
            If you are a parent or guardian and you have any concern about the data we hold or
            process, please write to{' '}
            <a className="text-accent hover:underline" href="mailto:support@mydyslexiatools.com">
              support@mydyslexiatools.com
            </a>
            . We will respond within seven days.
          </p>
        </Section>

        <Section title="How long we keep things">
          <ul>
            <li>Anonymous events: 24 months, then deleted.</li>
            <li>Photos sent for AI mode: zero. Processed and deleted within seconds.</li>
            <li>
              Emails you send us: kept for as long as we need to help you, then deleted within 12
              months.
            </li>
          </ul>
        </Section>

        <Section title="Lawful basis">
          <p>
            Where the law treats anonymous usage events as personal data (because they are paired
            with the random device identifier), the lawful basis for processing is our legitimate
            interest in improving the app. You can object by switching telemetry off in Settings.
          </p>
          <p>
            For AI mode, the lawful basis is our legitimate interest in providing a working
            worksheet recognition feature. The first-run notice in the app makes clear that AI
            mode is on by default and shows how to turn it off in Settings. The photo is
            processed and immediately discarded, with no copies kept by us or by Anthropic.
            You can withdraw at any time by switching AI mode off.
          </p>
        </Section>

        <Section title="Your rights">
          <p>You have the right to:</p>
          <ul>
            <li>Ask what data we hold about you.</li>
            <li>
              Ask us to correct or delete data. (Because we do not store identifiable data, this
              is usually as simple as resetting your app or clearing the device storage.)
            </li>
            <li>Object to our processing.</li>
            <li>
              Complain to the Information Commissioner's Office. Their website is{' '}
              <a className="text-accent hover:underline" href="https://ico.org.uk" rel="noopener noreferrer" target="_blank">
                ico.org.uk
              </a>
              .
            </li>
          </ul>
          <p>
            To exercise any of these rights, email{' '}
            <a className="text-accent hover:underline" href="mailto:support@mydyslexiatools.com">
              support@mydyslexiatools.com
            </a>
            .
          </p>
        </Section>

        <Section title="Third parties">
          <p>
            We use the smallest possible list of third parties. As of {LAST_UPDATED} the list is:
          </p>
          <ul>
            <li>
              <strong>Vercel</strong> hosts the website and the serverless function for AI mode.
              Vercel is our data processor for the duration of a request and stores nothing
              from us about the user.
            </li>
            <li>
              <strong>Anthropic</strong> provides the vision model behind AI mode. They are our
              processor for the duration of the request. The photo is not used to train their
              models.
            </li>
            <li>
              <strong>Supabase</strong> stores the anonymous events table (hosted in the European
              Union).
            </li>
            <li>
              <strong>Apple</strong> and <strong>Google</strong> provide the operating system
              speech recognition used when you dictate an answer. They handle the audio.
            </li>
          </ul>
          <p>
            If we ever add a third party, this list is updated and the "Last updated" date at
            the top of this page changes.
          </p>
        </Section>

        <Section title="Changes to this policy">
          <p>
            We may update this policy from time to time. The "Last updated" date at the top
            tells you when. Material changes will be announced in the app the next time you open
            it. If you do not agree to changes, you can stop using the app.
          </p>
        </Section>

        <Section title="Contact">
          <p>
            Questions, requests, or concerns:{' '}
            <a className="text-accent hover:underline" href="mailto:support@mydyslexiatools.com">
              support@mydyslexiatools.com
            </a>
            .
          </p>
        </Section>
      </article>
    </MarketingShell>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="mt-10">
      <h2 className="text-2xl font-semibold tracking-tight">{title}</h2>
      <div className="mt-3 leading-relaxed text-ink/80 space-y-3 [&_h3]:font-semibold [&_h3]:text-ink [&_h3]:mt-5 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:space-y-1 [&_a]:underline-offset-2">
        {children}
      </div>
    </section>
  );
}
